import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Terminal, 
  Copy, 
  Check,
  Star,
  StarOff,
  Trash2,
  RefreshCw,
  Cpu,
  HardDrive,
  Network,
  Users,
  Settings,
  Activity
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  system_info: Settings,
  cpu_memory: Cpu,
  disk: HardDrive,
  network: Network,
  services: Activity,
  users: Users
};

export default function CommandCenter() {
  const [osType, setOsType] = useState("linux");
  const [quickCommands, setQuickCommands] = useState({});
  const [savedCommands, setSavedCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [activeTab, setActiveTab] = useState("quick");

  useEffect(() => {
    fetchQuickCommands();
    fetchSavedCommands();
  }, [osType]);

  const fetchQuickCommands = async () => {
    try {
      const response = await axios.get(`${API}/quick-commands/${osType}`);
      setQuickCommands(response.data);
    } catch (error) {
      console.error("Failed to fetch quick commands:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedCommands = async () => {
    try {
      const response = await axios.get(`${API}/commands?os_type=${osType}`);
      setSavedCommands(response.data);
    } catch (error) {
      console.error("Failed to fetch saved commands:", error);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Command copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const saveCommand = async (cmd, desc, category) => {
    try {
      await axios.post(`${API}/commands`, {
        command: cmd,
        description: desc,
        os_type: osType,
        category: category,
        is_favorite: false
      });
      toast.success("Command saved");
      fetchSavedCommands();
    } catch (error) {
      toast.error("Failed to save command");
    }
  };

  const toggleFavorite = async (commandId) => {
    try {
      await axios.put(`${API}/commands/${commandId}/favorite`);
      fetchSavedCommands();
    } catch (error) {
      toast.error("Failed to update favorite");
    }
  };

  const deleteCommand = async (commandId) => {
    try {
      await axios.delete(`${API}/commands/${commandId}`);
      toast.success("Command deleted");
      fetchSavedCommands();
    } catch (error) {
      toast.error("Failed to delete command");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="command-center-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">Command Center</h1>
          <p className="text-zinc-400 text-sm mt-1">Quick commands and command history</p>
        </div>
        <Select value={osType} onValueChange={setOsType}>
          <SelectTrigger className="w-32 bg-black/50 border-white/10 text-white" data-testid="cmd-os-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-white/10">
            <SelectItem value="linux">Linux</SelectItem>
            <SelectItem value="windows">Windows</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-black/30 border border-white/10">
          <TabsTrigger value="quick" className="data-[state=active]:bg-blue-600" data-testid="quick-commands-tab">
            Quick Commands
          </TabsTrigger>
          <TabsTrigger value="saved" className="data-[state=active]:bg-blue-600" data-testid="saved-commands-tab">
            Saved ({savedCommands.length})
          </TabsTrigger>
          <TabsTrigger value="favorites" className="data-[state=active]:bg-blue-600" data-testid="favorites-tab">
            Favorites ({savedCommands.filter(c => c.is_favorite).length})
          </TabsTrigger>
        </TabsList>

        {/* Quick Commands */}
        <TabsContent value="quick" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(quickCommands).map(([category, data]) => {
                const Icon = categoryIcons[category] || Terminal;
                return (
                  <Card key={category} className="bg-[#0c0c0e] border-white/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                        <Icon className="w-5 h-5 text-blue-400" />
                        {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {data.commands.map((cmd, idx) => (
                        <div
                          key={idx}
                          className="group p-3 bg-black/30 rounded-lg border border-white/5 hover:border-white/10 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-zinc-400 mb-1">{cmd.desc}</p>
                              <code className="text-sm text-green-400 font-mono break-all">{cmd.cmd}</code>
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-white/10"
                                onClick={() => copyToClipboard(cmd.cmd, `${category}-${idx}`)}
                              >
                                {copiedId === `${category}-${idx}` ? (
                                  <Check className="w-3 h-3 text-green-400" />
                                ) : (
                                  <Copy className="w-3 h-3 text-zinc-400" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-white/10"
                                onClick={() => saveCommand(cmd.cmd, cmd.desc, category)}
                              >
                                <Star className="w-3 h-3 text-zinc-400" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Saved Commands */}
        <TabsContent value="saved" className="space-y-4">
          {savedCommands.length === 0 ? (
            <Card className="bg-[#0c0c0e] border-white/5">
              <CardContent className="py-12 text-center">
                <Terminal className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No saved commands yet. Save commands from Quick Commands tab.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {savedCommands.map((cmd) => (
                <Card key={cmd.id} className="bg-[#0c0c0e] border-white/5">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 font-mono">
                            {cmd.category}
                          </span>
                          <span className="text-xs text-zinc-500">
                            {new Date(cmd.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-300 mb-2">{cmd.description}</p>
                        <code className="text-sm text-green-400 font-mono bg-black/30 px-2 py-1 rounded block overflow-x-auto">
                          {cmd.command}
                        </code>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-white/10"
                          onClick={() => copyToClipboard(cmd.command, cmd.id)}
                        >
                          {copiedId === cmd.id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4 text-zinc-400" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-white/10"
                          onClick={() => toggleFavorite(cmd.id)}
                        >
                          {cmd.is_favorite ? (
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          ) : (
                            <StarOff className="w-4 h-4 text-zinc-400" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-red-500/20"
                          onClick={() => deleteCommand(cmd.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Favorites */}
        <TabsContent value="favorites" className="space-y-4">
          {savedCommands.filter(c => c.is_favorite).length === 0 ? (
            <Card className="bg-[#0c0c0e] border-white/5">
              <CardContent className="py-12 text-center">
                <Star className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No favorite commands yet. Star your frequently used commands.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {savedCommands.filter(c => c.is_favorite).map((cmd) => (
                <Card key={cmd.id} className="bg-[#0c0c0e] border-white/5 border-l-2 border-l-yellow-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-zinc-300 mb-2">{cmd.description}</p>
                        <code className="text-sm text-green-400 font-mono bg-black/30 px-2 py-1 rounded block overflow-x-auto">
                          {cmd.command}
                        </code>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-white/10"
                          onClick={() => copyToClipboard(cmd.command, cmd.id)}
                        >
                          {copiedId === cmd.id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4 text-zinc-400" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-white/10"
                          onClick={() => toggleFavorite(cmd.id)}
                        >
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
